import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInquiries, getCustomers, getSites, createInquiry, updateInquiry, deleteInquiry, createSubInquiry, createCustomer, createSite, createOrder, getOrders, createFollowup, getUsers, getEditHistory } from '../services/api';
import CustomDropdown from './CustomDropdown';
import CommentSection from './CommentSection';
import { useResponsive } from '../hooks/useResponsive';
import { getModalStyles } from '../utils/responsiveStyles';
import { generateQuote } from '../utils/quote';
import { canDelete } from '../utils/auth';
import { evaluateMathExpression } from '../utils/mathCalculator';

function Inquiries() {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPartialConversionModal, setShowPartialConversionModal] = useState(false);
  const [partialConversionData, setPartialConversionData] = useState([]);
  const [associatedOrderStatus, setAssociatedOrderStatus] = useState(null);
  const [inquiryOrderStatuses, setInquiryOrderStatuses] = useState({});
  const [formData, setFormData] = useState({
    customerId: '',
    siteId: '',
    type: 'common',
    remarks: '',
    paymentDaysFromDelivery: '',
    advancePaymentPercentage: '',
    transportCharge: '',
    loadingUnloadingCharge: '',
    loadingGst: 18,
    items: [{ productName: '', specification: '', quantity: '', unit: 'pcs', quotedPrice: '', gstPercentage: '', remarks: '' }]
  });
  const [showSiteAddressFields, setShowSiteAddressFields] = useState(false);
  const [newSiteData, setNewSiteData] = useState({
    siteName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    contactPerson: '',
    phone: ''
  });
  const [users, setUsers] = useState([]);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [followupFormData, setFollowupFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    assignedTo: '',
    referenceType: 'inquiry',
    referenceId: '',
    referenceNumber: ''
  });
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteModalData, setQuoteModalData] = useState({
    inquiry: null,
    gstPercentage: '',
    transportCharge: '',
    loadingUnloadingCharge: '',
    loadingGst: 18
  });
  const [editHistory, setEditHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');

  const { isMobile } = useResponsive();
  const modalStyles = getModalStyles(isMobile);

  useEffect(() => {
    loadInquiries();
    loadCustomers();
    loadUsers();
  }, []);

  const loadInquiries = async () => {
    try {
      const data = await getInquiries();
      setInquiries(data);

      // Load order statuses for converted inquiries
      const orderStatuses = {};
      for (const inquiry of data) {
        if (inquiry.status === 'converted') {
          try {
            const orders = await getOrders({ inquiryId: inquiry.id });
            if (orders && orders.length > 0) {
              // Get the latest order's status
              orderStatuses[inquiry.id] = orders[0].status;
            }
          } catch (error) {
            console.error(`Error loading order status for inquiry ${inquiry.id}:`, error);
          }
        }
      }
      setInquiryOrderStatuses(orderStatuses);
    } catch (error) {
      alert('Error loading inquiries: ' + error.message);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      alert('Error loading customers: ' + error.message);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Load edit history when selectedInquiry changes
  useEffect(() => {
    const loadEditHistory = async () => {
      if (selectedInquiry && selectedInquiry.id) {
        try {
          const history = await getEditHistory('inquiries', selectedInquiry.id);
          setEditHistory(history);
        } catch (error) {
          console.error('Error loading edit history:', error);
          setEditHistory([]);
        }
      } else {
        setEditHistory([]);
      }
    };

    loadEditHistory();
  }, [selectedInquiry]);

  const loadSitesForCustomer = async (customerId) => {
    try {
      const data = await getSites(customerId);
      setSites(data);
      setFormData(prev => ({ ...prev, customerId, siteId: '' }));
    } catch (error) {
      alert('Error loading sites: ' + error.message);
    }
  };

  const handleCreateCustomer = async (customerName) => {
    try {
      const newCustomer = await createCustomer({
        name: customerName,
        contactPerson: '',
        phone: '',
        email: '',
        address: ''
      });
      // Reload customers and select the newly created one
      await loadCustomers();
      setFormData(prev => ({ ...prev, customerId: newCustomer.id, siteId: '' }));
      setSites([]);
      return newCustomer;
    } catch (error) {
      alert('Error creating customer: ' + error.message);
      throw error;
    }
  };

  const handleCreateSite = async (siteName) => {
    if (!formData.customerId) {
      alert('Please select a customer first');
      throw new Error('No customer selected');
    }

    try {
      // Use detailed site data if address fields are shown, otherwise just site name
      const siteData = showSiteAddressFields
        ? { ...newSiteData, siteName: newSiteData.siteName || siteName }
        : {
            siteName,
            address: '',
            contactPerson: '',
            phone: ''
          };

      const newSite = await createSite({ ...siteData, customerId: formData.customerId });

      // Reload sites for current customer and select the newly created one
      const updatedSites = await getSites(formData.customerId);
      setSites(updatedSites);
      setFormData(prev => ({ ...prev, siteId: newSite.id }));

      // Reset site address fields
      setShowSiteAddressFields(false);
      setNewSiteData({
        siteName: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        contactPerson: '',
        phone: ''
      });

      return newSite;
    } catch (error) {
      alert('Error creating site: ' + error.message);
      throw error;
    }
  };

  const toggleSiteAddressFields = () => {
    setShowSiteAddressFields(!showSiteAddressFields);
    if (!showSiteAddressFields) {
      // Clear any existing site selection when showing address fields
      setFormData(prev => ({ ...prev, siteId: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that a site is selected
    if (!formData.siteId) {
      alert('Please select or create a site before submitting the inquiry.');
      return;
    }

    // Process items to evaluate any math expressions
    const processedFormData = {
      ...formData,
      items: formData.items.map(item => {
        const quantityEval = evaluateMathExpression(item.quantity);
        const priceEval = evaluateMathExpression(item.quotedPrice);

        return {
          ...item,
          quantity: quantityEval.isValid && quantityEval.result !== null ? quantityEval.result : item.quantity,
          quotedPrice: priceEval.isValid && priceEval.result !== null ? priceEval.result : item.quotedPrice
        };
      })
    };

    try {
      if (editingInquiry) {
        await updateInquiry(editingInquiry.id, processedFormData);
      } else {
        await createInquiry(processedFormData);
      }

      setShowForm(false);
      setEditingInquiry(null);
      setFormData({
        customerId: '',
        siteId: '',
        type: 'common',
        remarks: '',
        paymentDaysFromDelivery: '',
        advancePaymentPercentage: '',
        transportCharge: '',
        loadingCharge: '',
        unloadingCharge: '',
        items: [{ productName: '', specification: '', quantity: '', unit: 'pcs', quotedPrice: '', gstPercentage: '', remarks: '' }]
      });
      setShowSiteAddressFields(false);
      setNewSiteData({
        siteName: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        contactPerson: '',
        phone: ''
      });
      loadInquiries();
    } catch (error) {
      alert(`Error ${editingInquiry ? 'updating' : 'creating'} inquiry: ` + error.message);
    }
  };

  const handleDelete = async (id) => {
    // Get current user
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      alert('User session not found. Please log in again.');
      return;
    }

    let user;
    try {
      user = JSON.parse(userStr);
    } catch (error) {
      alert('Error reading user session. Please log in again.');
      return;
    }

    // Check if user is admin
    if (user.role !== 'admin') {
      alert('Only administrators can delete inquiries.');
      return;
    }

    // Find the inquiry to get its details
    const inquiry = inquiries.find(inq => inq.id === id);
    const inquiryNumber = inquiry?.inquiryNumber || 'this inquiry';

    // Show comprehensive warning
    const confirmMessage = `⚠️ WARNING: This action cannot be undone!\n\n` +
      `You are about to permanently delete inquiry ${inquiryNumber} and ALL related records:\n\n` +
      `• All sub-inquiries (if any)\n` +
      `• All associated orders\n` +
      `• All delivery challans\n` +
      `• All followups\n` +
      `• All comments\n\n` +
      `This will affect all data connected to this inquiry.\n\n` +
      `Are you absolutely sure you want to proceed?`;

    if (window.confirm(confirmMessage)) {
      try {
        const result = await deleteInquiry(id);

        // Show success message with details
        if (result.deleted) {
          const details = `Successfully deleted:\n\n` +
            `• Inquiry: ${result.deleted.inquiry}\n` +
            `• Sub-inquiries: ${result.deleted.subInquiries}\n` +
            `• Orders: ${result.deleted.orders}\n` +
            `• Deliveries: ${result.deleted.deliveries}\n` +
            `• Followups: ${result.deleted.followups}`;
          alert(details);
        } else {
          alert('Inquiry deleted successfully');
        }

        loadInquiries();
      } catch (error) {
        alert('Error deleting inquiry: ' + error.message);
      }
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { productName: '', specification: '', quantity: '', unit: 'pcs', quotedPrice: '', gstPercentage: '', remarks: '' }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index][field] = value;
      return { ...prev, items: newItems };
    });
  };

  const isInquiryEditable = (inquiry) => {
    // Block editing if inquiry is lost
    if (inquiry.status === 'lost') {
      return false;
    }

    // If inquiry is converted, check the order status
    if (inquiry.status === 'converted') {
      const orderStatus = inquiryOrderStatuses[inquiry.id];
      if (!orderStatus) return true; // If we don't know the status, allow edit (will be blocked in handleEdit)

      // Block editing if order is ready for dispatch or beyond (allow editing when preparing)
      const nonEditableStatuses = ['ready_for_dispatch', 'dispatched', 'delivered', 'payment_pending', 'closed'];
      return !nonEditableStatuses.includes(orderStatus);
    }
    return true;
  };

  const handleEdit = async (inquiry) => {
    setEditingInquiry(inquiry);
    setAssociatedOrderStatus(null);

    // Load sites for the customer
    try {
      const sitesData = await getSites(inquiry.customerId);
      setSites(sitesData);
    } catch (error) {
      console.error('Error loading sites:', error);
    }

    // If inquiry is converted, check the order status
    if (inquiry.status === 'converted') {
      try {
        const orders = await getOrders({ inquiryId: inquiry.id });
        if (orders && orders.length > 0) {
          // Get the latest order's status
          const latestOrder = orders[0];
          setAssociatedOrderStatus(latestOrder.status);

          // If order is ready for dispatch or beyond, prevent editing (allow editing when preparing)
          if (latestOrder.status === 'ready_for_dispatch' ||
              latestOrder.status === 'dispatched' ||
              latestOrder.status === 'delivered' ||
              latestOrder.status === 'payment_pending' ||
              latestOrder.status === 'closed') {
            alert(`Cannot edit inquiry: The associated order (${latestOrder.orderNumber || 'Order'}) is already in dispatch/delivered stage.\n\nPlease make changes directly in the Orders section.`);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking order status:', error);
      }
    }

    // Populate form with inquiry data
    setFormData({
      customerId: inquiry.customerId,
      siteId: inquiry.siteId,
      type: inquiry.type || 'common',
      remarks: inquiry.remarks || '',
      paymentDaysFromDelivery: inquiry.paymentDaysFromDelivery || '',
      advancePaymentPercentage: inquiry.advancePaymentPercentage || '',
      transportCharge: inquiry.transportCharge || '',
      loadingUnloadingCharge: inquiry.loadingUnloadingCharge || '',
      loadingGst: inquiry.loadingGst || 18,
      items: inquiry.items && inquiry.items.length > 0
        ? inquiry.items.map(item => ({
            productName: item.productName || '',
            specification: item.specification || '',
            quantity: item.quantity || '',
            unit: item.unit || 'pcs',
            quotedPrice: item.quotedPrice || '',
            gstPercentage: item.gstPercentage || '',
            remarks: item.remarks || ''
          }))
        : [{ productName: '', specification: '', quantity: '', unit: 'pcs', quotedPrice: '', gstPercentage: '', remarks: '' }]
    });

    setShowForm(true);
    setShowSiteAddressFields(false);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const checkAllItemsHaveQuotedPrice = (inquiry) => {
    if (!inquiry.items || inquiry.items.length === 0) {
      return false;
    }
    return inquiry.items.every(item => item.quotedPrice && item.quotedPrice !== null && item.quotedPrice !== '');
  };

  const handleConvertToOrder = async (inquiry) => {
    if (!checkAllItemsHaveQuotedPrice(inquiry)) {
      const missingItems = inquiry.items
        .filter(item => !item.quotedPrice || item.quotedPrice === null || item.quotedPrice === '')
        .map(item => item.productName)
        .join(', ');
      alert(`Cannot convert to order. The following items are missing quoted prices:\n\n${missingItems}\n\nPlease edit the inquiry and add quoted prices for all items.`);
      return;
    }

    if (window.confirm('Are you sure you want to convert this inquiry to an order?')) {
      try {
        // Prepare order items from all inquiry items
        const orderItems = inquiry.items.map(item => ({
          productName: item.productName,
          specification: item.specification,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          unitPrice: parseFloat(item.quotedPrice),
          amount: parseFloat(item.quantity) * parseFloat(item.quotedPrice),
          gst: 18, // Default GST
          remarks: item.remarks || ''
        }));

        // Create order
        const orderData = {
          customerId: inquiry.customerId,
          siteId: inquiry.siteId,
          inquiryId: inquiry.id,
          items: orderItems,
          transportCharge: inquiry.transportCharge || 0,
          loadingUnloadingCharge: inquiry.loadingUnloadingCharge || 0,
          loadingGst: inquiry.loadingGst || 18,
          expectedDeliveryDate: null,
          paymentTerms: '',
          deliveryTerms: '',
          remarks: `Converted from inquiry ${inquiry.inquiryNumber}`
        };

        await createOrder(orderData);

        // Update inquiry status to converted
        await updateInquiry(inquiry.id, { status: 'converted' });

        setShowDetailModal(false);
        setSelectedInquiry(null);
        loadInquiries();

        alert('Successfully converted inquiry to order!');
      } catch (error) {
        alert('Error converting inquiry to order: ' + error.message);
      }
    }
  };

  const handleMarkAsLost = async (inquiry) => {
    if (window.confirm('Are you sure you want to mark this inquiry as lost?')) {
      try {
        await updateInquiry(inquiry.id, { status: 'lost' });
        setShowDetailModal(false);
        setSelectedInquiry(null);
        loadInquiries();
      } catch (error) {
        alert('Error marking inquiry as lost: ' + error.message);
      }
    }
  };

  const handleDragStart = (e, inquiry) => {
    e.dataTransfer.setData('inquiryId', inquiry.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();

    // Reset column styling
    e.currentTarget.style.background = '#f8f9fa';
    e.currentTarget.style.border = 'none';

    const inquiryId = e.dataTransfer.getData('inquiryId');
    const inquiry = inquiries.find(i => i.id === inquiryId);

    if (!inquiry || inquiry.status === newStatus) {
      return;
    }

    // Prevent converted inquiries from being moved to any status except 'lost'
    if (inquiry.status === 'converted' && newStatus !== 'lost') {
      alert('A converted inquiry cannot be moved back to other statuses. It can only be marked as "lost" if needed.');
      return;
    }

    // Validate if converting to order
    if (newStatus === 'converted') {
      if (!checkAllItemsHaveQuotedPrice(inquiry)) {
        const missingItems = inquiry.items
          .filter(item => !item.quotedPrice || item.quotedPrice === null || item.quotedPrice === '')
          .map(item => item.productName)
          .join(', ');
        alert(`Cannot convert to order. The following items are missing quoted prices:\n\n${missingItems}\n\nPlease edit the inquiry and add quoted prices for all items.`);
        return;
      }

      if (!window.confirm('Are you sure you want to convert this inquiry to an order?')) {
        return;
      }

      // Create order from inquiry
      try {
        const orderItems = inquiry.items.map(item => ({
          productName: item.productName,
          specification: item.specification,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          unitPrice: parseFloat(item.quotedPrice),
          amount: parseFloat(item.quantity) * parseFloat(item.quotedPrice),
          gst: 18,
          remarks: item.remarks || ''
        }));

        const orderData = {
          customerId: inquiry.customerId,
          siteId: inquiry.siteId,
          inquiryId: inquiry.id,
          items: orderItems,
          transportCharge: inquiry.transportCharge || 0,
          loadingUnloadingCharge: inquiry.loadingUnloadingCharge || 0,
          loadingGst: inquiry.loadingGst || 18,
          expectedDeliveryDate: null,
          paymentTerms: '',
          deliveryTerms: '',
          remarks: `Converted from inquiry ${inquiry.inquiryNumber}`
        };

        await createOrder(orderData);
        await updateInquiry(inquiryId, { status: newStatus });
        loadInquiries();
        return;
      } catch (error) {
        alert('Error converting inquiry to order: ' + error.message);
        return;
      }
    }

    // Confirm if marking as lost
    if (newStatus === 'lost' && !window.confirm('Are you sure you want to mark this inquiry as lost?')) {
      return;
    }

    try {
      await updateInquiry(inquiryId, { status: newStatus });
      loadInquiries();
    } catch (error) {
      alert('Error updating inquiry status: ' + error.message);
    }
  };

  const handlePartialConversion = async (inquiry, selectedItems) => {
    try {
      // Prepare items for sub-inquiry creation
      const subInquiryItems = selectedItems.map(item => ({
        id: item.id,
        quantity: parseFloat(item.orderQuantity)
      }));

      // Create sub-inquiry with selected items
      const { subInquiry } = await createSubInquiry(inquiry.id, subInquiryItems);

      // Prepare order items from sub-inquiry items
      const orderItems = subInquiry.items.map(item => ({
        productName: item.productName,
        specification: item.specification,
        quantity: parseFloat(item.quantity),
        unit: item.unit,
        unitPrice: parseFloat(item.quotedPrice),
        amount: parseFloat(item.quantity) * parseFloat(item.quotedPrice),
        gst: 18, // Default GST, can be made configurable
        remarks: item.remarks || ''
      }));

      // Create order from sub-inquiry
      const orderData = {
        customerId: subInquiry.customerId,
        siteId: subInquiry.siteId,
        inquiryId: subInquiry.id,
        items: orderItems,
        transportCharge: inquiry.transportCharge || 0,
        loadingCharge: inquiry.loadingCharge || 0,
        unloadingCharge: inquiry.unloadingCharge || 0,
        expectedDeliveryDate: null,
        paymentTerms: inquiry.paymentDaysFromDelivery ? `Payment within ${inquiry.paymentDaysFromDelivery} days from delivery` : '',
        deliveryTerms: '',
        remarks: `Partial conversion from inquiry ${inquiry.inquiryNumber || inquiry.id}`
      };

      await createOrder(orderData);

      // Mark sub-inquiry as converted
      await updateInquiry(subInquiry.id, {
        status: 'converted'
      });

      // Close modals and reload
      setShowPartialConversionModal(false);
      setShowDetailModal(false);
      setPartialConversionData([]);
      setSelectedInquiry(null);
      loadInquiries();

      alert(`Successfully created sub-inquiry ${subInquiry.inquiryNumber} and converted to order. Parent inquiry ${inquiry.inquiryNumber} has been updated with remaining items.`);
    } catch (error) {
      alert('Error during partial conversion: ' + error.message);
    }
  };

  const getStageInfo = (status) => {
    const stages = {
      pending: { label: 'Pending', color: '#f39c12', icon: '', step: 1 },
      quoted: { label: 'Quotation Sent', color: '#3498db', icon: '', step: 2 },
      converted: { label: 'Converted to Order', color: '#27ae60', icon: '', step: 3 },
      lost: { label: 'Inquiry Lost', color: '#95a5a6', icon: '', step: 0 },
      cancelled: { label: 'Cancelled', color: '#e74c3c', icon: '', step: 0 }
    };
    return stages[status] || { label: status, color: '#95a5a6', icon: '', step: 0 };
  };

  const getStatusBadge = (status) => {
    const info = getStageInfo(status);

    return (
      <span style={{
        padding: '6px 12px',
        background: info.color,
        color: 'white',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {info.label}
      </span>
    );
  };

  const handleCreateFollowup = (inquiry) => {
    setFollowupFormData({
      title: `Follow up on inquiry ${inquiry.inquiryNumber}`,
      description: '',
      dueDate: '',
      assignedTo: '',
      referenceType: 'inquiry',
      referenceId: inquiry.id,
      referenceNumber: inquiry.inquiryNumber
    });
    setShowFollowupModal(true);
  };

  const handleFollowupSubmit = async (e) => {
    e.preventDefault();
    try {
      await createFollowup(followupFormData);
      setShowFollowupModal(false);
      setFollowupFormData({
        title: '',
        description: '',
        dueDate: '',
        assignedTo: '',
        referenceType: 'inquiry',
        referenceId: '',
        referenceNumber: ''
      });
      alert('Follow-up created successfully!');
    } catch (error) {
      alert('Error creating follow-up: ' + error.message);
    }
  };

  const handleDownloadQuote = (inquiry) => {
    const customer = customers.find(c => c.id === inquiry.customerId);
    const site = sites.find(s => s.id === inquiry.siteId);

    // For non-common inquiries, check if charges are already filled
    if (inquiry.type === 'non-common') {
      const hasTransportCharge = inquiry.transportCharge && inquiry.transportCharge !== '' && inquiry.transportCharge !== '0';
      const hasLoadingCharge = inquiry.loadingUnloadingCharge && inquiry.loadingUnloadingCharge !== '' && inquiry.loadingUnloadingCharge !== '0';
      const hasLoadingGst = inquiry.loadingGst && inquiry.loadingGst !== '' && inquiry.loadingGst !== '0';

      // Only show modal if any values are missing
      if (!hasTransportCharge || !hasLoadingCharge || !hasLoadingGst) {
        setQuoteModalData({
          inquiry: inquiry,
          gstPercentage: '',
          transportCharge: inquiry.transportCharge || '',
          loadingUnloadingCharge: inquiry.loadingUnloadingCharge || '',
          loadingGst: inquiry.loadingGst || 18
        });
        setShowQuoteModal(true);
      } else {
        // All values are present, directly generate quote
        generateQuote(inquiry, customer, site);
      }
    } else {
      // For common inquiries, directly generate quote
      generateQuote(inquiry, customer, site);
    }
  };

  const handleQuoteModalSubmit = (e) => {
    e.preventDefault();
    const customer = customers.find(c => c.id === quoteModalData.inquiry.customerId);
    const site = sites.find(s => s.id === quoteModalData.inquiry.siteId);

    const additionalCharges = {
      gstPercentage: quoteModalData.gstPercentage,
      transportCharge: quoteModalData.transportCharge,
      loadingUnloadingCharge: quoteModalData.loadingUnloadingCharge,
      loadingGst: quoteModalData.loadingGst
    };

    generateQuote(quoteModalData.inquiry, customer, site, additionalCharges);

    setShowQuoteModal(false);
    setQuoteModalData({
      inquiry: null,
      gstPercentage: '',
      transportCharge: '',
      loadingUnloadingCharge: '',
      loadingGst: 18
    });
  };

  const columns = [
    { key: 'pending', label: 'Pending Inquiries', icon: '', color: '#f39c12' },
    { key: 'converted', label: 'Converted to Order', icon: '', color: '#27ae60' },
    { key: 'lost', label: 'Inquiry Lost', icon: '', color: '#95a5a6' }
  ];

  const getFilteredInquiries = () => {
    let filtered = inquiries;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(inquiry => {
        const customerName = getCustomerName(inquiry.customerId).toLowerCase();
        const inquiryNumber = (inquiry.inquiryNumber || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        // Search in inquiry number, customer name, and items
        const matchesBasic = inquiryNumber.includes(searchLower) || customerName.includes(searchLower);

        // Search in items
        const matchesItems = inquiry.items?.some(item =>
          (item.productName || '').toLowerCase().includes(searchLower) ||
          (item.specification || '').toLowerCase().includes(searchLower)
        );

        return matchesBasic || matchesItems;
      });
    }

    // Month filter
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(inquiry => {
        const inquiryDate = new Date(inquiry.createdAt);
        const [year, month] = selectedMonth.split('-');
        return inquiryDate.getFullYear() === parseInt(year) &&
               inquiryDate.getMonth() === parseInt(month) - 1;
      });
    }

    return filtered;
  };

  const getInquiriesByStatus = (status) => {
    const filtered = getFilteredInquiries();
    return filtered.filter(inquiry => inquiry.status === status);
  };

  const renderInquiryCard = (inquiry) => {
    return (
      <div
        key={inquiry.id}
        draggable
        onDragStart={(e) => {
          handleDragStart(e, inquiry);
          e.currentTarget.style.opacity = '0.5';
          e.currentTarget.style.cursor = 'grabbing';
        }}
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #ecf0f1',
          transition: 'all 0.2s ease',
          cursor: 'grab'
        }}
        onClick={async (e) => {
          setSelectedInquiry(inquiry);
          setShowDetailModal(true);
          // Load sites for this inquiry's customer
          if (inquiry.customerId) {
            try {
              const customerSites = await getSites(inquiry.customerId);
              setSites(customerSites);
            } catch (error) {
              console.error('Error loading sites:', error);
            }
          }
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        onDragEnd={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.cursor = 'grab';
        }}
      >
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontWeight: 'bold', color: '#3D1F1F', fontSize: '14px', marginBottom: '4px' }}>
            {inquiry.inquiryNumber}
          </div>
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
            {getCustomerName(inquiry.customerId)}
          </div>
        </div>

        <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #ecf0f1' }}>
          {inquiry.items && inquiry.items.length > 0 && (
            <>
              {inquiry.items.slice(0, 2).map((item, idx) => (
                <div key={idx} style={{ fontSize: '12px', color: '#555', marginBottom: '3px' }}>
                  • {item.productName} ({item.quantity} {item.unit})
                </div>
              ))}
              {inquiry.items.length > 2 && (
                <div style={{ fontSize: '11px', color: '#7f8c8d', fontStyle: 'italic' }}>
                  +{inquiry.items.length - 2} more items
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '10px' }}>
          {new Date(inquiry.createdAt).toLocaleDateString()}
        </div>

        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {isInquiryEditable(inquiry) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(inquiry);
              }}
              style={{
                ...buttonStyle,
                padding: '6px 12px',
                fontSize: '12px',
                margin: 0,
                background: '#3498db'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2980b9'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#3498db'}
            >
              Edit
            </button>
          )}
          {inquiry.status === 'pending' && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConvertToOrder(inquiry);
                }}
                style={{
                  ...buttonStyle,
                  padding: '6px 12px',
                  fontSize: '12px',
                  margin: 0,
                  background: checkAllItemsHaveQuotedPrice(inquiry) ? '#27ae60' : '#95a5a6',
                  opacity: checkAllItemsHaveQuotedPrice(inquiry) ? 1 : 0.7
                }}
                onMouseEnter={(e) => {
                  if (checkAllItemsHaveQuotedPrice(inquiry)) {
                    e.currentTarget.style.background = '#229954';
                  }
                }}
                onMouseLeave={(e) => {
                  if (checkAllItemsHaveQuotedPrice(inquiry)) {
                    e.currentTarget.style.background = '#27ae60';
                  }
                }}
              >
                Convert
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedInquiry(inquiry);
                  const initialData = inquiry.items.map(item => ({
                    ...item,
                    selected: false,
                    orderQuantity: item.quantity
                  }));
                  setPartialConversionData(initialData);
                  setShowPartialConversionModal(true);
                }}
                style={{
                  ...buttonStyle,
                  padding: '6px 12px',
                  fontSize: '12px',
                  margin: 0,
                  background: '#9b59b6'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#8e44ad'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#9b59b6'}
              >
                Partial
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateFollowup(inquiry);
                }}
                style={{
                  ...buttonStyle,
                  padding: '6px 12px',
                  fontSize: '12px',
                  margin: 0,
                  background: '#16a085'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#138d75'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#16a085'}
              >
                Follow-up
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadQuote(inquiry);
                }}
                style={{
                  ...buttonStyle,
                  padding: '6px 12px',
                  fontSize: '12px',
                  margin: 0,
                  background: checkAllItemsHaveQuotedPrice(inquiry) ? '#e67e22' : '#95a5a6',
                  opacity: checkAllItemsHaveQuotedPrice(inquiry) ? 1 : 0.7
                }}
                onMouseEnter={(e) => {
                  if (checkAllItemsHaveQuotedPrice(inquiry)) {
                    e.currentTarget.style.background = '#d35400';
                  }
                }}
                onMouseLeave={(e) => {
                  if (checkAllItemsHaveQuotedPrice(inquiry)) {
                    e.currentTarget.style.background = '#e67e22';
                  }
                }}
                disabled={!checkAllItemsHaveQuotedPrice(inquiry)}
                title={!checkAllItemsHaveQuotedPrice(inquiry) ? "Add quoted prices to all items first" : "Download quotation PDF"}
              >
                Quote
              </button>
            </>
          )}
          {canDelete() && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(inquiry.id);
              }}
              style={{
                ...buttonStyle,
                padding: '6px 12px',
                fontSize: '12px',
                margin: 0,
                background: '#e74c3c'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#c0392b'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#e74c3c'}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    );
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    marginBottom: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    padding: '10px 20px',
    background: '#C8342B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    marginRight: '10px',
    fontWeight: '500'
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#3D1F1F' }}>Inquiries</h1>
        <button
          onClick={() => {
            setEditingInquiry(null);
            setShowForm(true);
            setShowSiteAddressFields(false);
            setFormData({
              customerId: '',
              siteId: '',
              type: 'common',
              remarks: '',
              items: [{ productName: '', specification: '', quantity: '', unit: 'pcs', quotedPrice: '', remarks: '' }]
            });
            setNewSiteData({
              siteName: '',
              address: '',
              city: '',
              state: '',
              pincode: '',
              contactPerson: '',
              phone: ''
            });
          }}
          style={buttonStyle}
        >
          Create Inquiry
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div style={{
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <div style={{ flex: isMobile ? '1 1 100%' : 2 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search by inquiry number, customer, or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div style={{ flex: isMobile ? '1 1 100%' : 1 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Month
          </label>
          <CustomDropdown
            value={selectedMonth}
            onChange={(value) => setSelectedMonth(value)}
            options={[
              { value: 'all', label: 'All Months' },
              ...(() => {
                const months = [];
                const now = new Date();
                for (let i = 0; i < 12; i++) {
                  const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  months.push({ value: `${year}-${month}`, label });
                }
                return months;
              })()
            ]}
            placeholder="All Months"
          />
        </div>
      </div>

      {/* Inquiry Form Modal */}
      {showForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => {
            setShowForm(false);
            setEditingInquiry(null);
            setFormData({
              customerId: '',
              siteId: '',
              type: 'common',
              remarks: '',
              paymentDaysFromDelivery: '',
              advancePaymentPercentage: '',
              items: [{ productName: '', specification: '', quantity: '', unit: 'pcs', quotedPrice: '', remarks: '' }]
            });
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '750px',
              maxHeight: '90vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              boxSizing: 'border-box'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>{editingInquiry ? 'Edit Inquiry' : 'Create New Inquiry'}</h2>
            {editingInquiry && editingInquiry.status === 'converted' && isInquiryEditable(editingInquiry) && (
              <div style={{
                padding: '12px 16px',
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                marginBottom: '15px',
                fontSize: '14px',
                color: '#856404',
                lineHeight: '1.6'
              }}>
                <strong>⚠️ Note:</strong> This inquiry has been converted to an order.
                <br />
                • <strong>Customer and site</strong> cannot be changed.
                <br />
                • <strong>Items, quantities, and prices</strong> can still be edited (order hasn't reached production stage yet).
              </div>
            )}
            <form onSubmit={handleSubmit} style={{ width: '100%', boxSizing: 'border-box' }}>
              <CustomDropdown
                value={formData.customerId}
                onChange={(value) => {
                  if (editingInquiry && editingInquiry.status === 'converted') {
                    alert('Cannot change customer: This inquiry has been converted to an order.');
                    return;
                  }
                  loadSitesForCustomer(value);
                }}
                options={customers.map(customer => ({
                  value: customer.id,
                  label: customer.name
                }))}
                placeholder="Select Customer *"
                style={{ marginBottom: '10px', width: '100%', boxSizing: 'border-box' }}
                required
                disabled={editingInquiry && editingInquiry.status === 'converted'}
                creatable={!editingInquiry || editingInquiry.status !== 'converted'}
                onCreate={handleCreateCustomer}
                createLabel="Create customer"
              />

              <div style={{ marginBottom: '10px', width: '100%', boxSizing: 'border-box' }}>
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  marginBottom: '8px',
                  flexWrap: 'wrap'
                }}>
                  {!showSiteAddressFields && (
                    <div style={{ flex: 1, minWidth: '200px', boxSizing: 'border-box' }}>
                      <CustomDropdown
                        value={formData.siteId}
                        onChange={(value) => {
                          if (editingInquiry && editingInquiry.status === 'converted') {
                            alert('Cannot change site: This inquiry has been converted to an order.');
                            return;
                          }
                          setFormData(prev => ({ ...prev, siteId: value }));
                        }}
                        options={sites.map(site => {
                          const siteName = site.site_name || site.siteName || site.name || 'Unnamed Site';
                          const address = site.address || '';
                          const city = site.city || '';
                          const state = site.state || '';

                          // Build a readable label with site name and address
                          let label = siteName;
                          const addressParts = [address, city, state].filter(part => part);
                          if (addressParts.length > 0) {
                            label += ` - ${addressParts.join(', ')}`;
                          }

                          return {
                            value: site.id,
                            label: label
                          };
                        })}
                        placeholder="Select Site *"
                        style={{ marginBottom: '0' }}
                        required={!showSiteAddressFields}
                        disabled={!formData.customerId || (editingInquiry && editingInquiry.status === 'converted')}
                      />
                    </div>
                  )}
                  {formData.customerId && !(editingInquiry && editingInquiry.status === 'converted') && (
                    <button
                      type="button"
                      onClick={toggleSiteAddressFields}
                      style={{
                        ...buttonStyle,
                        padding: '8px 16px',
                        fontSize: '13px',
                        background: showSiteAddressFields ? '#95a5a6' : '#27ae60',
                        margin: 0,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {showSiteAddressFields ? '✕ Cancel New Site' : '+ Add New Site'}
                    </button>
                  )}
                </div>

                {/* Site Address Form */}
                {showSiteAddressFields && (
                  <div style={{
                    border: '2px solid #27ae60',
                    borderRadius: '8px',
                    padding: '20px',
                    background: '#f0f9f4',
                    marginTop: '10px',
                    boxSizing: 'border-box',
                    width: '100%'
                  }}>
                    <h3 style={{ margin: '0 0 15px 0', color: '#27ae60', fontSize: '16px' }}>
                      New Site Address Details
                    </h3>
                    <input
                      style={{
                        ...inputStyle,
                        boxSizing: 'border-box'
                      }}
                      type="text"
                      placeholder="Site Name *"
                      value={newSiteData.siteName}
                      onChange={(e) => setNewSiteData({ ...newSiteData, siteName: e.target.value })}
                      required={showSiteAddressFields}
                    />
                    <textarea
                      style={{
                        ...inputStyle,
                        minHeight: '60px',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                      placeholder="Address *"
                      value={newSiteData.address}
                      onChange={(e) => setNewSiteData({ ...newSiteData, address: e.target.value })}
                      required={showSiteAddressFields}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <input
                        style={{
                          ...inputStyle,
                          boxSizing: 'border-box'
                        }}
                        type="text"
                        placeholder="City"
                        value={newSiteData.city}
                        onChange={(e) => setNewSiteData({ ...newSiteData, city: e.target.value })}
                      />
                      <input
                        style={{
                          ...inputStyle,
                          boxSizing: 'border-box'
                        }}
                        type="text"
                        placeholder="State"
                        value={newSiteData.state}
                        onChange={(e) => setNewSiteData({ ...newSiteData, state: e.target.value })}
                      />
                    </div>
                    <input
                      style={{
                        ...inputStyle,
                        boxSizing: 'border-box'
                      }}
                      type="text"
                      placeholder="Pincode"
                      value={newSiteData.pincode}
                      onChange={(e) => setNewSiteData({ ...newSiteData, pincode: e.target.value })}
                    />
                    <input
                      style={{
                        ...inputStyle,
                        boxSizing: 'border-box'
                      }}
                      type="text"
                      placeholder="Contact Person"
                      value={newSiteData.contactPerson}
                      onChange={(e) => setNewSiteData({ ...newSiteData, contactPerson: e.target.value })}
                    />
                    <input
                      style={{
                        ...inputStyle,
                        boxSizing: 'border-box'
                      }}
                      type="tel"
                      placeholder="Phone"
                      value={newSiteData.phone}
                      onChange={(e) => setNewSiteData({ ...newSiteData, phone: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!newSiteData.siteName || !newSiteData.address) {
                          alert('Please fill in Site Name and Address before creating the site.');
                          return;
                        }
                        try {
                          await handleCreateSite(newSiteData.siteName);
                          alert('Site created successfully!');
                        } catch (error) {
                          // Error already shown by handleCreateSite
                        }
                      }}
                      style={{
                        ...buttonStyle,
                        background: '#27ae60',
                        width: '100%',
                        margin: '10px 0 0 0',
                        padding: '12px 20px',
                        fontSize: '15px',
                        fontWeight: '600'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#229954'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#27ae60'}
                    >
                      ✓ Create Site for Customer
                    </button>
                  </div>
                )}
              </div>

              {/* Inquiry Type Selector */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#2c3e50'
                }}>
                  Inquiry Type *
                </label>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#3D1F1F',
                    padding: '10px 15px',
                    border: formData.type === 'common' ? '2px solid #C8342B' : '2px solid #ddd',
                    borderRadius: '6px',
                    background: formData.type === 'common' ? '#FFF5F5' : 'white',
                    transition: 'all 0.2s ease',
                    flex: 1,
                    justifyContent: 'center'
                  }}>
                    <input
                      type="radio"
                      name="inquiryType"
                      value="common"
                      checked={formData.type === 'common'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      style={{
                        accentColor: '#C8342B',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ fontWeight: formData.type === 'common' ? '600' : '400' }}>
                      Common
                    </span>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#3D1F1F',
                    padding: '10px 15px',
                    border: formData.type === 'non-common' ? '2px solid #C8342B' : '2px solid #ddd',
                    borderRadius: '6px',
                    background: formData.type === 'non-common' ? '#FFF5F5' : 'white',
                    transition: 'all 0.2s ease',
                    flex: 1,
                    justifyContent: 'center'
                  }}>
                    <input
                      type="radio"
                      name="inquiryType"
                      value="non-common"
                      checked={formData.type === 'non-common'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      style={{
                        accentColor: '#C8342B',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ fontWeight: formData.type === 'non-common' ? '600' : '400' }}>
                      Non-Common
                    </span>
                  </label>
                </div>
              </div>

              <textarea
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                placeholder="Remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              />

              <h3 style={{ marginTop: '20px', marginBottom: '10px', color: '#C8342B' }}>Payment Terms</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Payment Days from Delivery <span style={{ color: '#C8342B' }}>*</span>
                  </label>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="Days after delivery"
                    value={formData.paymentDaysFromDelivery}
                    onChange={(e) => setFormData({ ...formData, paymentDaysFromDelivery: e.target.value })}
                    min="0"
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>Required to convert to order</small>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Advance Payment %
                  </label>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="Advance percentage"
                    value={formData.advancePaymentPercentage}
                    onChange={(e) => setFormData({ ...formData, advancePaymentPercentage: e.target.value })}
                    min="0"
                    max="100"
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>Optional (0-100%)</small>
                </div>
              </div>

              <h3 style={{ marginTop: '20px', marginBottom: '10px', color: '#C8342B' }}>Additional Charges</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Transport Charge
                  </label>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="0.00"
                    value={formData.transportCharge}
                    onChange={(e) => setFormData({ ...formData, transportCharge: e.target.value })}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Loading/Unloading Charge
                  </label>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="0.00"
                    value={formData.loadingUnloadingCharge}
                    onChange={(e) => setFormData({
                      ...formData,
                      loadingUnloadingCharge: e.target.value
                    })}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Loading GST %
                  </label>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="18"
                    value={formData.loadingGst}
                    onChange={(e) => setFormData({
                      ...formData,
                      loadingGst: e.target.value
                    })}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Items</h3>
              {formData.items.map((item, index) => (
                <div key={index} style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '15px',
                  marginBottom: '15px',
                  background: '#f9f9f9',
                  boxSizing: 'border-box',
                  width: '100%'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    gap: '10px',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}>
                    <h4 style={{ margin: 0 }}>Item {index + 1}</h4>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        style={{
                          ...buttonStyle,
                          padding: '5px 10px',
                          background: '#e74c3c',
                          margin: 0,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <input
                    style={inputStyle}
                    type="text"
                    placeholder="Product Name *"
                    value={item.productName}
                    onChange={(e) => updateItem(index, 'productName', e.target.value)}
                    required
                  />
                  <input
                    style={inputStyle}
                    type="text"
                    placeholder="Specification"
                    value={item.specification}
                    onChange={(e) => updateItem(index, 'specification', e.target.value)}
                  />
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr',
                    gap: '10px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <div style={{ position: 'relative' }}>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <input
                            style={inputStyle}
                            type="text"
                            placeholder="Quantity * (e.g., 8*4 or 10+5)"
                            value={item.quantity}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              updateItem(index, 'quantity', inputValue);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.shiftKey) {
                                const evaluation = evaluateMathExpression(item.quantity);
                                if (evaluation.isValid && evaluation.result !== null) {
                                  updateItem(index, 'quantity', evaluation.result.toString());
                                  e.preventDefault();
                                }
                              }
                            }}
                            required
                          />
                          {item.quantity && (() => {
                            const evaluation = evaluateMathExpression(item.quantity);
                            if (evaluation.isValid && item.quantity !== evaluation.result.toString()) {
                              return (
                                <div style={{
                                  position: 'absolute',
                                  left: '9px',
                                  top: '9px',
                                  pointerEvents: 'none',
                                  fontSize: '14px',
                                  lineHeight: 'normal',
                                  whiteSpace: 'pre',
                                  fontFamily: 'inherit'
                                }}>
                                  <span style={{ opacity: 0 }}>{item.quantity}</span>
                                  <span style={{ color: '#95a5a6', fontWeight: '500' }}> = {evaluation.result}</span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        {item.quantity && (() => {
                          const evaluation = evaluateMathExpression(item.quantity);
                          if (evaluation.isValid && item.quantity !== evaluation.result.toString()) {
                            return (
                              <button
                                type="button"
                                onClick={() => updateItem(index, 'quantity', evaluation.result.toString())}
                                style={{
                                  padding: '8px 12px',
                                  background: '#27ae60',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap',
                                  height: '38px'
                                }}
                                title={`Use calculated value: ${evaluation.result}`}
                              >
                                = {evaluation.result}
                              </button>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {item.quantity && (() => {
                        const evaluation = evaluateMathExpression(item.quantity);
                        if (evaluation.isValid && item.quantity !== evaluation.result.toString()) {
                          return (
                            <div style={{
                              fontSize: '10px',
                              color: '#7f8c8d',
                              marginTop: '2px',
                              fontStyle: 'italic'
                            }}>
                              Click button or press Shift+Enter to use {evaluation.result}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div style={{ width: '100%', boxSizing: 'border-box' }}>
                      <CustomDropdown
                        value={item.unit}
                        onChange={(value) => updateItem(index, 'unit', value)}
                        options={[
                          { value: 'pcs', label: 'Pcs' },
                          { value: 'sq ft', label: 'Sq Ft' },
                          { value: 'sq m', label: 'Sq M' },
                          { value: 'kg', label: 'Kg' },
                          { value: 'sheets', label: 'Sheets' },
                          { value: 'rft', label: 'RFT' },
                          { value: 'cft', label: 'CFT' },
                          { value: 'bundle', label: 'Bundle' },
                          { value: 'box', label: 'Box' }
                        ]}
                        placeholder="Unit"
                        style={{ marginBottom: '0' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#2c3e50',
                      marginBottom: '4px'
                    }}>
                      Per Unit Quoted Price (Required for converting to order)
                    </label>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input
                          style={inputStyle}
                          type="text"
                          placeholder="Enter price (e.g., 100*1.5 or 50+25)"
                          value={item.quotedPrice}
                          onChange={(e) => updateItem(index, 'quotedPrice', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.shiftKey) {
                              const evaluation = evaluateMathExpression(item.quotedPrice);
                              if (evaluation.isValid && evaluation.result !== null) {
                                updateItem(index, 'quotedPrice', evaluation.result.toString());
                                e.preventDefault();
                              }
                            }
                          }}
                        />
                        {item.quotedPrice && (() => {
                          const evaluation = evaluateMathExpression(item.quotedPrice);
                          if (evaluation.isValid && item.quotedPrice !== evaluation.result.toString()) {
                            return (
                              <div style={{
                                position: 'absolute',
                                left: '9px',
                                top: '9px',
                                pointerEvents: 'none',
                                fontSize: '14px',
                                lineHeight: 'normal',
                                whiteSpace: 'pre',
                                fontFamily: 'inherit'
                              }}>
                                <span style={{ opacity: 0 }}>{item.quotedPrice}</span>
                                <span style={{ color: '#95a5a6', fontWeight: '500' }}> = {evaluation.result}</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {item.quotedPrice && (() => {
                        const evaluation = evaluateMathExpression(item.quotedPrice);
                        if (evaluation.isValid && item.quotedPrice !== evaluation.result.toString()) {
                          return (
                            <button
                              type="button"
                              onClick={() => updateItem(index, 'quotedPrice', evaluation.result.toString())}
                              style={{
                                padding: '8px 12px',
                                background: '#27ae60',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                height: '38px'
                              }}
                              title={`Use calculated value: ${evaluation.result}`}
                            >
                              = {evaluation.result}
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    {item.quotedPrice && (() => {
                      const evaluation = evaluateMathExpression(item.quotedPrice);
                      if (evaluation.isValid && item.quotedPrice !== evaluation.result.toString()) {
                        return (
                          <div style={{
                            fontSize: '10px',
                            color: '#7f8c8d',
                            marginTop: '2px',
                            fontStyle: 'italic'
                          }}>
                            Click button or press Shift+Enter to use {evaluation.result}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  {formData.type === 'common' && (
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#2c3e50',
                        marginBottom: '4px'
                      }}>
                        GST (%)
                      </label>
                      <input
                        style={inputStyle}
                        type="number"
                        placeholder="Enter GST percentage"
                        value={item.gstPercentage}
                        onChange={(e) => updateItem(index, 'gstPercentage', e.target.value)}
                        step="0.01"
                        min="0"
                        max="100"
                      />
                    </div>
                  )}
                  <input
                    style={inputStyle}
                    type="text"
                    placeholder="Item Remarks"
                    value={item.remarks}
                    onChange={(e) => updateItem(index, 'remarks', e.target.value)}
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={addItem}
                style={{ ...buttonStyle, background: '#27ae60', marginBottom: '20px' }}
              >
                + Add Item
              </button>

              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '5px',
                paddingTop: '15px',
                borderTop: '1px solid #ecf0f1',
                flexWrap: 'wrap'
              }}>
                <button type="submit" style={buttonStyle}>
                  {editingInquiry ? 'Update Inquiry' : 'Create Inquiry'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingInquiry(null);
                    setShowSiteAddressFields(false);
                    setNewSiteData({
                      siteName: '',
                      address: '',
                      city: '',
                      state: '',
                      pincode: '',
                      contactPerson: '',
                      phone: ''
                    });
                  }}
                  style={{ ...buttonStyle, background: '#95a5a6' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inquiry Kanban Board */}
      {inquiries.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: isMobile ? '20px' : '40px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ color: '#7f8c8d', fontSize: isMobile ? '14px' : '16px' }}>
            No inquiries found. Create your first inquiry!
          </p>
        </div>
      ) : (
        <div style={{
          display: isMobile ? 'flex' : 'grid',
          gridTemplateColumns: isMobile ? 'none' : 'repeat(3, 1fr)',
          gap: isMobile ? '12px' : '20px',
          alignItems: 'start',
          overflowX: isMobile ? 'auto' : 'visible',
          paddingBottom: isMobile ? '10px' : '0',
          WebkitOverflowScrolling: 'touch'
        }}>
          {columns.map(column => {
            const inquiriesInColumn = getInquiriesByStatus(column.key);

            return (
              <div
                key={column.key}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.key)}
                onDragEnter={(e) => {
                  e.currentTarget.style.background = '#e8f4f8';
                  e.currentTarget.style.border = '2px dashed ' + column.color;
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.border = 'none';
                }}
                style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: isMobile ? '12px' : '15px',
                  height: isMobile ? 'calc(100vh - 150px)' : 'calc(100vh - 200px)',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease',
                  minWidth: isMobile ? '280px' : 'auto',
                  flexShrink: isMobile ? 0 : 1
                }}
              >
                <div style={{
                  marginBottom: '15px',
                  paddingBottom: '12px',
                  borderBottom: `3px solid ${column.color}`,
                  flexShrink: 0
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '5px'
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#3D1F1F'
                    }}>
                      {column.label}
                    </h3>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#7f8c8d',
                    fontWeight: '600'
                  }}>
                    {inquiriesInColumn.length} {inquiriesInColumn.length === 1 ? 'inquiry' : 'inquiries'}
                  </div>
                </div>

                <div style={{
                  overflowY: 'auto',
                  flex: 1,
                  paddingRight: '5px'
                }}>
                  {inquiriesInColumn.map(inquiry => renderInquiryCard(inquiry))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedInquiry && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowDetailModal(false);
            setSelectedInquiry(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '25px',
              borderBottom: '2px solid #ecf0f1',
              background: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: '12px 12px 0 0'
            }}>
              <div>
                <h2 style={{ margin: 0, color: '#3D1F1F', fontSize: '20px' }}>
                  Inquiry Details
                </h2>
                <div style={{ fontSize: '14px', color: '#7f8c8d', marginTop: '5px' }}>
                  {selectedInquiry.inquiryNumber}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedInquiry(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#95a5a6',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#e74c3c'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#95a5a6'}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '25px' }}>
              <div style={{ marginBottom: '25px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                      CUSTOMER
                    </label>
                    <div style={{ fontSize: '15px', color: '#3D1F1F', fontWeight: '600' }}>
                      {getCustomerName(selectedInquiry.customerId)}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                      STATUS
                    </label>
                    {getStatusBadge(selectedInquiry.status)}
                  </div>
                </div>

                {selectedInquiry.siteId && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                      SITE
                    </label>
                    {(() => {
                      const site = sites.find(s => s.id === selectedInquiry.siteId);
                      if (!site) {
                        return (
                          <div style={{ fontSize: '14px', color: '#e74c3c', fontWeight: '500' }}>
                            Site not found
                          </div>
                        );
                      }

                      const siteName = site.site_name || site.siteName || site.name || 'Unnamed Site';
                      const addressParts = [
                        site.address,
                        site.city,
                        site.state,
                        site.pincode
                      ].filter(Boolean);

                      return (
                        <div>
                          <div style={{ fontSize: '15px', color: '#3D1F1F', fontWeight: '600', marginBottom: '6px' }}>
                            {siteName}
                          </div>
                          {addressParts.length > 0 && (
                            <div style={{ fontSize: '13px', color: '#7f8c8d', lineHeight: '1.5' }}>
                              {addressParts.join(', ')}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                    CREATED DATE
                  </label>
                  <div style={{ fontSize: '14px', color: '#3D1F1F' }}>
                    {new Date(selectedInquiry.createdAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                </div>

                {selectedInquiry.remarks && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                      REMARKS
                    </label>
                    <div style={{ fontSize: '14px', color: '#3D1F1F', background: '#f8f9fa', padding: '10px', borderRadius: '6px' }}>
                      {selectedInquiry.remarks}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#3D1F1F', borderBottom: '2px solid #ecf0f1', paddingBottom: '10px' }}>
                  Items ({selectedInquiry.items?.length || 0})
                </h3>
                {selectedInquiry.items && selectedInquiry.items.length > 0 ? (
                  <div>
                    {selectedInquiry.items.map((item, idx) => {
                      const hasQuotedPrice = item.quotedPrice && item.quotedPrice !== null && item.quotedPrice !== '';
                      return (
                        <div key={idx} style={{
                          background: hasQuotedPrice ? '#f8f9fa' : '#fff5f5',
                          borderRadius: '8px',
                          padding: '15px',
                          marginBottom: '10px',
                          border: hasQuotedPrice ? '1px solid #ecf0f1' : '2px solid #e74c3c'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ fontWeight: '600', color: '#3D1F1F', fontSize: '15px' }}>
                              {item.productName}
                            </div>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', fontWeight: '600' }}>
                              {item.quantity} {item.unit}
                            </div>
                          </div>
                          {item.specification && (
                            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '5px' }}>
                              {item.specification}
                            </div>
                          )}
                          {hasQuotedPrice ? (
                            <div>
                              <div style={{ fontSize: '14px', color: '#27ae60', fontWeight: '600', marginTop: '8px' }}>
                                Per Unit Quoted Price: ₹{parseFloat(item.quotedPrice).toFixed(2)}
                              </div>
                              {selectedInquiry.type === 'common' && item.gstPercentage && (
                                <div style={{ fontSize: '13px', color: '#3498db', fontWeight: '600', marginTop: '4px' }}>
                                  GST: {item.gstPercentage}%
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ fontSize: '13px', color: '#e74c3c', fontWeight: '600', marginTop: '8px' }}>
                              No quoted price
                            </div>
                          )}
                          {item.remarks && (
                            <div style={{ fontSize: '12px', color: '#95a5a6', fontStyle: 'italic', marginTop: '5px' }}>
                              Note: {item.remarks}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#7f8c8d', textAlign: 'center', padding: '20px' }}>No items</p>
                )}
              </div>

              {!isInquiryEditable(selectedInquiry) && (
                <div style={{
                  padding: '12px 16px',
                  background: '#e8f4f8',
                  border: '1px solid #3498db',
                  borderRadius: '6px',
                  marginBottom: '10px',
                  fontSize: '13px',
                  color: '#2c3e50',
                  lineHeight: '1.6'
                }}>
                  <strong>ℹ️ Information:</strong> This inquiry cannot be edited because the associated order is in dispatch/delivered stage. Please make changes in the Orders section.
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', paddingTop: '15px', borderTop: '2px solid #ecf0f1', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleDownloadQuote(selectedInquiry)}
                  style={{
                    ...buttonStyle,
                    flex: 1,
                    minWidth: '150px',
                    background: '#8e44ad'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#7d3c98'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#8e44ad'}
                >
                  Download Quote
                </button>
                {isInquiryEditable(selectedInquiry) && (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleEdit(selectedInquiry);
                    }}
                    style={{
                      ...buttonStyle,
                      flex: 1,
                      minWidth: '100px',
                      background: '#3498db'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#2980b9'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#3498db'}
                  >
                    Edit
                  </button>
                )}
                {selectedInquiry.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleConvertToOrder(selectedInquiry)}
                      style={{
                        ...buttonStyle,
                        flex: 1,
                        minWidth: '150px',
                        background: checkAllItemsHaveQuotedPrice(selectedInquiry) ? '#27ae60' : '#95a5a6',
                        opacity: checkAllItemsHaveQuotedPrice(selectedInquiry) ? 1 : 0.7
                      }}
                      onMouseEnter={(e) => {
                        if (checkAllItemsHaveQuotedPrice(selectedInquiry)) {
                          e.currentTarget.style.background = '#229954';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (checkAllItemsHaveQuotedPrice(selectedInquiry)) {
                          e.currentTarget.style.background = '#27ae60';
                        }
                      }}
                    >
                      Convert to Order
                    </button>
                    <button
                      onClick={() => {
                        // Initialize partial conversion data
                        const initialData = selectedInquiry.items.map(item => ({
                          ...item,
                          selected: false,
                          orderQuantity: item.quantity
                        }));
                        setPartialConversionData(initialData);
                        setShowPartialConversionModal(true);
                      }}
                      style={{
                        ...buttonStyle,
                        flex: 1,
                        minWidth: '150px',
                        background: '#9b59b6'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#8e44ad'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#9b59b6'}
                    >
                      Partial Conversion
                    </button>
                    <button
                      onClick={() => handleMarkAsLost(selectedInquiry)}
                      style={{
                        ...buttonStyle,
                        flex: 1,
                        minWidth: '150px',
                        background: '#e67e22'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#d35400'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#e67e22'}
                    >
                      Mark as Lost
                    </button>
                  </>
                )}
                {selectedInquiry.status === 'converted' && (
                  <button
                    onClick={() => handleMarkAsLost(selectedInquiry)}
                    style={{
                      ...buttonStyle,
                      flex: 1,
                      minWidth: '150px',
                      background: '#e67e22'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d35400'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e67e22'}
                  >
                    Mark as Lost
                  </button>
                )}
              </div>

              {/* Edit History Section */}
              {editHistory && editHistory.length > 0 && (
                <div style={{ marginBottom: '25px', borderTop: '1px solid #dee2e6', paddingTop: '20px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3D1F1F', fontSize: '16px' }}>Edit History</h3>
                  <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                    {editHistory.map((entry, index) => (
                      <div key={entry.id || index} style={{
                        padding: '10px',
                        background: '#f8f9fa',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontWeight: '600', color: '#3D1F1F' }}>
                            {entry.fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <span style={{ color: '#7f8c8d', fontSize: '12px' }}>
                            {new Date(entry.changedAt).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <span style={{ color: '#e74c3c', textDecoration: 'line-through' }}>
                            {entry.oldValue || '(empty)'}
                          </span>
                          <span>→</span>
                          <span style={{ color: '#27ae60', fontWeight: '500' }}>
                            {entry.newValue || '(empty)'}
                          </span>
                        </div>
                        <div style={{ marginTop: '5px', color: '#7f8c8d', fontSize: '11px' }}>
                          Changed by: {entry.changedByName || 'Unknown'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comment Section */}
              <CommentSection
                referenceType="inquiry"
                referenceId={selectedInquiry.id}
                currentUserId={JSON.parse(localStorage.getItem('user') || '{}').id}
                currentUserName={JSON.parse(localStorage.getItem('user') || '{}').name}
              />
            </div>
          </div>
        </div>
      )}

      {/* Partial Conversion Modal */}
      {showPartialConversionModal && selectedInquiry && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowPartialConversionModal(false);
            setPartialConversionData([]);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '25px',
              borderBottom: '2px solid #ecf0f1',
              background: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: '12px 12px 0 0'
            }}>
              <div>
                <h2 style={{ margin: 0, color: '#3D1F1F', fontSize: '20px' }}>
                  Partial Conversion to Order
                </h2>
                <div style={{ fontSize: '14px', color: '#7f8c8d', marginTop: '5px' }}>
                  {selectedInquiry.inquiryNumber} - Select items and quantities to convert
                </div>
              </div>
              <button
                onClick={() => {
                  setShowPartialConversionModal(false);
                  setPartialConversionData([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#95a5a6',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#e74c3c'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#95a5a6'}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '25px' }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#3D1F1F', borderBottom: '2px solid #ecf0f1', paddingBottom: '10px' }}>
                  Select Items for Order
                </h3>
                {partialConversionData.map((item, idx) => {
                  const hasQuotedPrice = item.quotedPrice && item.quotedPrice !== null && item.quotedPrice !== '';
                  return (
                    <div key={idx} style={{
                      background: item.selected ? '#e8f8f5' : '#f8f9fa',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '10px',
                      border: item.selected ? '2px solid #27ae60' : '1px solid #ecf0f1'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                        <input
                          type="checkbox"
                          checked={item.selected}
                          onChange={(e) => {
                            const newData = [...partialConversionData];
                            newData[idx].selected = e.target.checked;
                            setPartialConversionData(newData);
                          }}
                          disabled={!hasQuotedPrice}
                          style={{
                            width: '20px',
                            height: '20px',
                            marginTop: '5px',
                            cursor: hasQuotedPrice ? 'pointer' : 'not-allowed',
                            accentColor: '#27ae60'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#3D1F1F', fontSize: '15px', marginBottom: '8px' }}>
                            {item.productName}
                            {!hasQuotedPrice && (
                              <span style={{ color: '#e74c3c', fontSize: '12px', marginLeft: '10px', fontWeight: 'normal' }}>
                                (No quoted price - cannot select)
                              </span>
                            )}
                          </div>
                          {item.specification && (
                            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '8px' }}>
                              {item.specification}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                              Available: <strong>{item.quantity} {item.unit}</strong>
                            </div>
                            {hasQuotedPrice && (
                              <div style={{ fontSize: '13px', color: '#27ae60', fontWeight: '600' }}>
                                Price: ₹{parseFloat(item.quotedPrice).toFixed(2)}
                              </div>
                            )}
                          </div>
                          {item.selected && (
                            <div style={{ marginTop: '10px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', display: 'block', marginBottom: '5px' }}>
                                Quantity to Convert to Order:
                              </label>
                              <input
                                type="number"
                                value={item.orderQuantity}
                                onChange={(e) => {
                                  const newData = [...partialConversionData];
                                  const value = parseFloat(e.target.value);
                                  if (value > 0 && value <= item.quantity) {
                                    newData[idx].orderQuantity = e.target.value;
                                    setPartialConversionData(newData);
                                  }
                                }}
                                min="0.01"
                                max={item.quantity}
                                step="0.01"
                                style={{
                                  width: '200px',
                                  padding: '8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              />
                              <span style={{ marginLeft: '10px', color: '#7f8c8d', fontSize: '14px' }}>
                                {item.unit}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: '10px', paddingTop: '15px', borderTop: '2px solid #ecf0f1', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    const selectedItems = partialConversionData.filter(item => item.selected);
                    if (selectedItems.length === 0) {
                      alert('Please select at least one item to convert to order.');
                      return;
                    }
                    handlePartialConversion(selectedInquiry, selectedItems);
                  }}
                  style={{
                    ...buttonStyle,
                    flex: 1,
                    minWidth: '150px',
                    background: partialConversionData.some(item => item.selected) ? '#27ae60' : '#95a5a6',
                    opacity: partialConversionData.some(item => item.selected) ? 1 : 0.7
                  }}
                  disabled={!partialConversionData.some(item => item.selected)}
                >
                  Convert Selected Items
                </button>
                <button
                  onClick={() => {
                    setShowPartialConversionModal(false);
                    setPartialConversionData([]);
                  }}
                  style={{
                    ...buttonStyle,
                    background: '#95a5a6',
                    flex: 1,
                    minWidth: '100px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowupModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowFollowupModal(false);
            setFollowupFormData({
              title: '',
              description: '',
              dueDate: '',
              assignedTo: '',
              referenceType: 'inquiry',
              referenceId: '',
              referenceNumber: ''
            });
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: '#3D1F1F' }}>Create Follow-up</h2>
            <form onSubmit={handleFollowupSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Title <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="text"
                  value={followupFormData.title}
                  onChange={(e) => setFollowupFormData({ ...followupFormData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Description
                </label>
                <textarea
                  value={followupFormData.description}
                  onChange={(e) => setFollowupFormData({ ...followupFormData, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Due Date <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="date"
                  value={followupFormData.dueDate}
                  onChange={(e) => setFollowupFormData({ ...followupFormData, dueDate: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Assigned To <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <CustomDropdown
                  value={followupFormData.assignedTo}
                  onChange={(value) => setFollowupFormData({ ...followupFormData, assignedTo: value })}
                  options={users.map(user => ({ value: user.id, label: user.name }))}
                  placeholder="Select User"
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Reference
                </label>
                <input
                  type="text"
                  value={followupFormData.referenceNumber}
                  disabled
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px',
                    background: '#f8f9fa',
                    color: '#7f8c8d'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowFollowupModal(false);
                    setFollowupFormData({
                      title: '',
                      description: '',
                      dueDate: '',
                      assignedTo: '',
                      referenceType: 'inquiry',
                      referenceId: '',
                      referenceNumber: ''
                    });
                  }}
                  style={{
                    ...buttonStyle,
                    background: '#95a5a6'
                  }}
                >
                  Cancel
                </button>
                <button type="submit" style={buttonStyle}>
                  Create Follow-up
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quote Modal for Non-Common Inquiries */}
      {showQuoteModal && quoteModalData.inquiry && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowQuoteModal(false);
            setQuoteModalData({
              inquiry: null,
              gstPercentage: '',
              transportCharge: '',
              loadingCharge: '',
              unloadingCharge: ''
            });
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: '#3D1F1F' }}>Generate Quote</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              Please provide GST percentage and additional charges for this quotation.
            </p>
            <form onSubmit={handleQuoteModalSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  GST (%) <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="number"
                  value={quoteModalData.gstPercentage}
                  onChange={(e) => setQuoteModalData({ ...quoteModalData, gstPercentage: e.target.value })}
                  required
                  min="0"
                  max="100"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter GST percentage (e.g., 18)"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Transport Charge
                </label>
                <input
                  type="number"
                  value={quoteModalData.transportCharge}
                  onChange={(e) => setQuoteModalData({ ...quoteModalData, transportCharge: e.target.value })}
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="0.00"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Loading/Unloading Charge
                </label>
                <input
                  type="number"
                  value={quoteModalData.loadingUnloadingCharge}
                  onChange={(e) => setQuoteModalData({
                    ...quoteModalData,
                    loadingUnloadingCharge: e.target.value
                  })}
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="0.00"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Loading GST %
                </label>
                <input
                  type="number"
                  value={quoteModalData.loadingGst}
                  onChange={(e) => setQuoteModalData({
                    ...quoteModalData,
                    loadingGst: e.target.value
                  })}
                  min="0"
                  max="100"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="18"
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuoteModal(false);
                    setQuoteModalData({
                      inquiry: null,
                      gstPercentage: '',
                      transportCharge: '',
                      loadingCharge: '',
                      unloadingCharge: ''
                    });
                  }}
                  style={{
                    ...buttonStyle,
                    background: '#95a5a6'
                  }}
                >
                  Cancel
                </button>
                <button type="submit" style={buttonStyle}>
                  Generate Quote
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inquiries;
